import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ProtectedRoute from '../features/auth/components/ProtectedRoute';
import AuthLayout from '../shared/components/layout/AuthLayout';
import { BETA_MODE } from '../shared/config/flags';
import { trackPageView } from '../shared/lib/analytics';

// Public pages
import LandingPage from '../features/landing/LandingPage';
import AboutPage from '../features/landing/pages/AboutPage';
import BetaRequestPage from '../features/landing/pages/BetaRequestPage';
import TermsPage from '../features/landing/pages/TermsPage';
import PrivacyPage from '../features/landing/pages/PrivacyPage';
import LoginPage from '../features/auth/LoginPage';
import SignUpPage from '../features/auth/SignUpPage';
import ForgotPasswordPage from '../features/auth/ForgotPasswordPage';
import ResetPasswordPage from '../features/auth/ResetPasswordPage';

// Protected pages
import PortfolioPage from '../features/portfolio/pages/PortfolioPage';
import SnapTradeCallbackPage from '../features/portfolio/pages/SnapTradeCallbackPage';
import PortfolioLayout from '../features/portfolio/layout/PortfolioLayout';
import { FriendsPage } from '../features/friends/pages/FriendsPage';
import { FriendPortfolioPage } from '../features/friends/pages/FriendPortfolioPage';
import SettingsPage from '../features/settings/pages/SettingsPage';
import ChatPage from '../features/chat/pages/ChatPage';

/**
 * App routes component with beta mode gating.
 * 
 * In beta mode (REACT_APP_BETA_MODE=true):
 * - Only marketing routes are accessible: /, /about, /beta-request
 * - All app routes (/login, /signup, /portfolio, etc.) redirect to landing page
 * 
 * In normal mode (REACT_APP_BETA_MODE=false or unset):
 * - All routes are accessible as normal
 */
export const AppRoutes: React.FC = () => {
  const location = useLocation();

  // Track page views on route changes
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  // Beta mode: Only allow marketing routes
  if (BETA_MODE) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/beta-request" element={<BetaRequestPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/brokerage/snaptrade/callback" element={<SnapTradeCallbackPage />} />
        {/* All other routes redirect to landing page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Normal mode: All routes accessible
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/beta-request" element={<BetaRequestPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/brokerage/snaptrade/callback" element={<SnapTradeCallbackPage />} />

      {/* Protected routes */}
      <Route
        path="/portfolio"
        element={
          <ProtectedRoute>
            <AuthLayout>
              <PortfolioLayout>
                <PortfolioPage />
              </PortfolioLayout>
            </AuthLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/friends"
        element={
          <ProtectedRoute>
            <FriendsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/friends/:friendId"
        element={
          <ProtectedRoute>
            <FriendPortfolioPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AuthLayout>
              <SettingsPage />
            </AuthLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};
